import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Inicialização do tema
    const [theme, setTheme] = useState<Theme>(() => {
        // 1. Verificar localStorage
        const savedTheme = localStorage.getItem('playmoment-theme') as Theme | null;
        if (savedTheme) return savedTheme;

        // 2. Verificar preferência do sistema
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }

        return 'light';
    });

    useEffect(() => {
        // Aplicar classe no elemento raiz para variáveis CSS
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(theme);

        // Persistir escolha
        localStorage.setItem('playmoment-theme', theme);
    }, [theme]);

    // Ouvir mudanças na preferência do sistema em tempo real caso o usuário não tenha definido manualmente
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (e: MediaQueryListEvent) => {
            // Só altera automaticamente se o usuário não tiver uma preferência salva (opcional)
            // Por simplicidade, vamos respeitar o estado local que já considera o sistema no boot
            if (!localStorage.getItem('playmoment-theme')) {
                setTheme(e.matches ? 'dark' : 'light');
            }
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const toggleTheme = () => {
        setTheme(prev => (prev === 'light' ? 'dark' : 'light'));
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
